import AppButton from '@nxsys/components/AppButton';
import { useDrawerSave } from './useDrawerSave';
import AppSelectSync from '@nxsys/components/AppSelectSync';
import AppLoader from '@nxsys/components/AppLoader';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useGetBackOfficeByEmployeeId,
  useUpdateBackOfficeByEmployeeId,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { Icons } from '@nxsys/types/icons';
import { showMessage } from '@nxsys/utils/message';
import { Form, Typography } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { UpdateBackOfficeByEmployeeIdParams } from '@nxsys/types/api';

type Props = {
  employeeId: string;
};

const BackOffice = ({ employeeId }: Props) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();

  const {
    run: fetchData,
    loading: isFetchingData,
    value,
  } = useGetBackOfficeByEmployeeId();
  const {
    run: updateData,
    loading: isUpdating,
    value: updateValue,
  } = useUpdateBackOfficeByEmployeeId();
  const data = useMemo(
    () => updateValue?.data ?? value?.data,
    [value, updateValue],
  );

  useEffect(() => {
    fetchData(employeeId);
  }, [employeeId]);

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        customerSuccessManager: data.customerSuccessManager?.map((item) => ({
          label: `${item.forename} ${item.surname}`,
          value: item.id,
          fullData: item,
        })),
        agencyConsultant: data.agencyConsultant?.map((item) => ({
          label: `${item.forename} ${item.surname}`,
          value: item.id,
          fullData: item,
        })),
        salesConsultant: data.salesConsultant?.map((item) => ({
          label: `${item.forename} ${item.surname}`,
          value: item.id,
          fullData: item,
        })),
        other: data.other?.map((item) => ({
          label: `${item.forename} ${item.surname}`,
          value: item.id,
          fullData: item,
        })),
      });
    }
  }, [data]);

  const [isSaving, setIsSaving] = useState(false);
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const values = await form.validateFields();
      const params: UpdateBackOfficeByEmployeeIdParams = {
        customerSuccessManager: values.customerSuccessManager?.map(
          (item: any) => item.fullData,
        ),
        agencyConsultant: values.agencyConsultant?.map(
          (item: any) => item.fullData,
        ),
        salesConsultant: values.salesConsultant?.map(
          (item: any) => item.fullData,
        ),
        other: values.other?.map((item: any) => item.fullData),
      };
      await updateData(employeeId, params);
      showMessage({
        type: 'success',
        content: messages['common.updateSuccess'],
      });
      fetchData(employeeId);
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  // NX-4682: in the quick-edit drawer, surface Save in the drawer header.
  const inDrawer = useDrawerSave(handleSave, isSaving);

  return (
    <div>
      {(isFetchingData || isUpdating) && <AppLoader />}
      <Typography.Title
        level={5}
        className="!mb-6 !text-[20px] !leading-[1.6] !tracking-[-0.02em] !text-[#1A1D1F]"
      >
        {messages['common.backOffice']}
      </Typography.Title>
      <Form form={form} layout="vertical">
        <Form.Item
          label={messages['common.customerSuccessManager']}
          name="customerSuccessManager"
        >
          <AppSelectSync
            type="admin"
            apiType="searchUserAdmin"
            placeholder={messages['form.select']}
            style={{ width: '100%' }}
            mode="multiple"
            onChangeValue={(value: any) => {
              form.setFieldValue('customerSuccessManager', value);
            }}
          />
        </Form.Item>
        <Form.Item
          label={messages['common.agencyConsultant']}
          name="agencyConsultant"
        >
          <AppSelectSync
            type="admin"
            apiType="searchUserAdmin"
            placeholder={messages['form.select']}
            style={{ width: '100%' }}
            mode="multiple"
            onChangeValue={(value: any) => {
              form.setFieldValue('agencyConsultant', value);
            }}
          />
        </Form.Item>
        <Form.Item
          label={messages['common.salesConsultant']}
          name="salesConsultant"
        >
          <AppSelectSync
            type="admin"
            apiType="searchUserAdmin"
            placeholder={messages['form.select']}
            style={{ width: '100%' }}
            mode="multiple"
            onChangeValue={(value: any) => {
              form.setFieldValue('salesConsultant', value);
            }}
          />
        </Form.Item>
        <Form.Item label={messages['common.other']} name="other">
          <AppSelectSync
            type="admin"
            apiType="searchUserAdmin"
            placeholder={messages['form.select']}
            style={{ width: '100%' }}
            mode="multiple"
            onChangeValue={(value: any) => {
              form.setFieldValue('other', value);
            }}
          />
        </Form.Item>
      </Form>
      {!inDrawer && (
        <div className="flex items-center justify-start gap-3 pt-4">
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={handleSave}
            loading={isSaving}
          />
        </div>
      )}
    </div>
  );
};

export default BackOffice;
